import {
  Component,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
} from '@angular/core';
import {
  ExperienciaProfissional,
  HabilitacaoAcademica,
  NivelLinguaEnum,
  Profissional,
  ProfissionalCompetenciaDetailsDTO,
  ProfissionalLingua,
} from '../../../api';
import { Button } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ActivatedRoute } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { getInitials } from '@shared/pipes/initials.pipe';
import {
  ProfissionalSectorService,
  ProfissionalLinguaService,
  ProfissionalCompetenciaService,
  HabilitacaoAcademicaService,
  ExperienciaProfissionalService,
  ProfissionalService,
} from '@app/features-modules/bolsa-de-profissionais/services/';
import { ApiService } from '@core/providers/http';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-detalhe-profissionais',
  imports: [CommonModule, Button, TableModule, AvatarModule],
  providers: [
    ApiService,
    ProfissionalSectorService,
    ProfissionalLinguaService,
    ProfissionalCompetenciaService,
    HabilitacaoAcademicaService,
    ExperienciaProfissionalService,
    MessageService,
  ],
  templateUrl: './detalhe-profissionais.component.html',
  styleUrl: './detalhe-profissionais.component.scss',
  standalone: true,
})
export class DetalheProfissionaisComponent implements OnInit {
  imageError = false;

  getInitials(name?: string | null): string {
    return getInitials(name);
  }
  private route = inject(ActivatedRoute);
  profissionalNotApiService = inject(ProfissionalService);
  @Output() onCloseModal = new EventEmitter<boolean>();
  habilitacaoService = inject(HabilitacaoAcademicaService);
  profCompetenciaService = inject(ProfissionalCompetenciaService);
  experienciaService = inject(ExperienciaProfissionalService);
  profissionalService = inject(ProfissionalSectorService);
  profLinguaService = inject(ProfissionalLinguaService);
  messageService = inject(MessageService);
  listaLinguas: ProfissionalLingua[] = [];
  profissional = input<Profissional>();
  listaHabilitacoes: HabilitacaoAcademica[] = [];
  listaExperiencias: ExperienciaProfissional[] = [];
  listaCompetencias: ProfissionalCompetenciaDetailsDTO[] = [];
  niveisLingua: any[] = [
    { id: NivelLinguaEnum.NUMBER_1, designacao: 'Básico' },
    { id: NivelLinguaEnum.NUMBER_2, designacao: 'Intermédio' },
    { id: NivelLinguaEnum.NUMBER_3, designacao: 'Avançado' },
  ];
  isAccountLoading = false;
  imagePreview: string | ArrayBuffer | null = null; // To store image as Base64 URL

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.getPhoto(id);
    }

    this.getExperiencias();
    this.getHabilitacoes();
    this.getProfissionalCompetencias();
    this.getLinguas();
  }

  closeModal() {
    this.onCloseModal.emit(true);
  }

  getNivelByEnum(nivel: NivelLinguaEnum): string {
    return (
      this.niveisLingua.find((x) => x.id == nivel)?.designacao ?? 'Desconhecido'
    );
  }

  podeReforcarConta(): boolean {
    const p = this.profissional();
    if (!p?.email) return false;

    // Heurísticas comuns — ajusta aos teus campos reais, se existirem
    const importadoSiasp =
      (p as any)?.origem === 'SIASP' ||
      (p as any)?.viaIntegracao === true ||
      (p as any)?.isImportadoSiasp === true;

    const semKeycloak =
      !(p as any)?.keycloakUserId &&
      !(p as any)?.usuarioKeycloakId &&
      !(p as any)?.hasAccount;

    // permitir reforçar se veio do SIASP ou se não vemos ID de conta
    return importadoSiasp || semKeycloak;
  }

  reforcarConta() {
    const email = this.profissional()?.email;
    if (!email) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Email em falta',
        detail: 'Associe um e-mail ao profissional para ativar a conta.',
        life: 3500,
      });
      return;
    }

    this.isAccountLoading = true;
    this.profissionalService.validar(email).subscribe({
      next: (res: any) => {
        this.isAccountLoading = false;

        // Normaliza cenários comuns de retorno da API
        const codigo = res?.retorno?.codigo ?? res?.status ?? 200;
        const msg = res?.retorno?.mensagem ?? res?.message;

        if (codigo === 200 || codigo === 201) {
          this.messageService.add({
            severity: 'success',
            summary: 'Conta reforçada',
            detail:
              msg ??
              'Credenciais enviadas. O profissional poderá completar o perfil no portal.',
            life: 4000,
          });
        } else if (codigo === 409) {
          this.messageService.add({
            severity: 'info',
            summary: 'Conta já existente',
            detail:
              msg ?? 'Este profissional já possui conta activa no Keycloak.',
            life: 4000,
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validação pendente',
            detail: msg ?? 'A validação foi solicitada. Verifique o e-mail.',
            life: 4000,
          });
        }
      },
      error: (err) => {
        this.isAccountLoading = false;
        console.error('reforcarConta error:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail:
            err?.error?.message ??
            'Não foi possível reforçar/ativar a conta neste momento.',
          life: 4500,
        });
      },
    });
  }

  getHabilitacoes() {
    const profissionalId = this.profissional()?.id;
    if (profissionalId !== undefined) {
      this.habilitacaoService
        .listByProfissionalId(String(profissionalId))
        .subscribe((result) => {
          if (result.retorno?.codigo === 200 && result.data) {
            this.listaHabilitacoes = result.data;
          }
        });
    }
  }

  getExperiencias() {
    const profissionalId = this.profissional()?.id;
    if (profissionalId !== undefined) {
      this.experienciaService
        .listByProfissionalId(String(profissionalId))
        .subscribe((result) => {
          if (result.retorno?.codigo === 200 && result.data) {
            this.listaExperiencias = result.data;
          }
        });
    }
  }

  getProfissionalCompetencias() {
    const profissionalId = this.profissional()?.id;
    if (profissionalId !== undefined) {
      this.profCompetenciaService
        .list({
          ProfissionalID: String(profissionalId),
        })
        .subscribe((result) => {
          if (result.retorno?.codigo === 200 && result.data) {
            this.listaCompetencias = result.data;
          }
        });
    }
  }

  getLinguas() {
    const profissionalId = this.profissional()?.id;
    if (profissionalId !== undefined) {
      this.profLinguaService
        .listByProfissionalId(String(profissionalId))
        .subscribe((result) => {
          if (result.retorno?.codigo === 200 && result.data) {
            this.listaLinguas = result.data;
          }
        });
    }
  }

  getPhoto(id: number) {
    this.profissionalService.getPhotoProfissionalIdGet(id).subscribe({
      next: (response) => {
        if (!response || !response.type.startsWith('image/')) {
          console.warn('Resposta não é imagem. A usar placeholder.');
          this.imagePreview = null;
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          this.imagePreview = reader.result;
        };
        reader.readAsDataURL(response);
      },
      error: (err) => {
        console.error(`Erro ao carregar imagem da empresa ${id}:`, err);
        this.imagePreview = null;
      },
    });
  }
}
